import { AxiosResponse, AxiosError } from 'axios';

import {
    HECTARE,
    HUNDRED_LITRE
} from 'scenes/Spray/scenes/Diary/components/SprayEventFinalForm/components/SprayEventForm/components/ProductPage/components/ProductRatePerUnitTypeDropdown/constants';
import { EntityName, EntityPathNames, EntityId } from 'services/entities/types';
import { DocumentSet } from 'services/documentSets/types';

// Constants
import {
    SPRAY_EVENTS_LOAD_REQUESTED,
    SPRAY_EVENTS_LOAD_SUCCEEDED,
    SPRAY_EVENTS_LOAD_FAILED,
    SPRAY_EVENTS_LOAD_CANCELED,
    SPRAY_EVENT_CREATE_REQUESTED,
    SPRAY_EVENT_CREATE_SUCCEEDED,
    SPRAY_EVENT_CREATE_FAILED,
    SPRAY_EVENTS_SET,
    SPRAY_EVENTS_DELETE,
    SPRAY_EVENT_SET,
    SPRAY_EVENT_DELETE_REQUESTED,
    SPRAY_EVENT_DELETE_FAILED,
    SPRAY_EVENT_DELETE_SUCCEEDED,
    SPRAY_EVENT_UPDATE_REQUESTED,
    SPRAY_EVENT_UPDATE_SUCCEEDED,
    SPRAY_EVENT_UPDATE_FAILED,
    SPRAY_EVENTS_COMPLETE_REQUESTED,
    SPRAY_EVENTS_COMPLETE_FAILED,
    SPRAY_EVENTS_COMPLETE_SUCCEEDED,
    SPRAY_EVENT_UNCOMPLETE_REQUESTED,
    SPRAY_EVENT_UNCOMPLETE_FAILED,
    SPRAY_EVENT_UNCOMPLETE_SUCCEEDED,
    EQUIPMENT_FILTER_TYPE,
    PRODUCT_FILTER_TYPE,
    DATE_RANGE_FILTER_TYPE,
    APPLICATION_RATE_FILTER_TYPE,
    DATE_FILTER_TYPE,
    ENTITIES_FILTER_TYPE,
    INNER_ENTITIES_FILTER_TYPE
} from './constants';

export type SprayProductId = number;
export type SprayProductOrderIndex = number;
export type SprayProductUnits = number;
export type SprayUnitsPerHundredLitre = number;
export type SprayUnitsPerTank = number;
export type SprayProductPerUnitType = typeof HECTARE | typeof HUNDRED_LITRE;

export type SprayProduct = {
    id: SprayProductId;
    orderIndex: SprayProductOrderIndex;
    units: SprayProductUnits;
    perUnitType: SprayProductPerUnitType;
};

export type SprayEventEntityId = number;
export type SprayEventUuid = string;
export type SprayEventId = number;
export type SprayEventSchemaId = number;
export type SprayEventStartDate = string;
export type SprayEventEndDate = string;
export type SprayEntityIds = number[];
export type SprayEquipmentSetId = number;
export type SprayProducts = SprayProduct[];
export type SprayWaterTotal = number;
export type SprayEventTotalArea = number;
export type SprayWaterRate = number;
export type SprayApplicationType = number;
export type SprayApplicationRate = number;
export type SprayWaterSourceId = number;
export type SprayMethod = string;
export type PersonEmail = string;
export type OperatorPersonId = number;
export type SprayApplicatorId = number;
export type SprayEventJustification = string;
export type SprayEventTechnicalPersonName = string;
export type SprayEventJobCompletedAt = Date;
export type SprayEventIsFinalSpray = boolean;
export type SprayEventIsReadyToSubmit = boolean;
export type SprayEventIsWeedSpray = boolean;
export type SprayEventIsSubmitted = boolean;
export type SprayEventEntities = { id: EntityId }[];
export type SprayEventTwoDimensionStartingRow = string;

export type SprayEventOperation = {
    operatorPersonId: OperatorPersonId;
    sprayEquipmentSetId: SprayEquipmentSetId;
    startingRow: SprayEventStartingRow | string;
    twoDimensionStartingRow?: SprayEventTwoDimensionStartingRow;
};

export type SprayEventOperations = SprayEventOperation[];

export enum SprayEventStartingRow {
    DOUBLE = 'DOUBLE',
    OUTSIDE = 'OUTSIDE',
    ANY = 'ANY'
}

export enum SprayEventApplicationType {
    STANDARD = 'standard',
    MULTIOPERATOR = 'multi',
    TWODIMENSION = '2D'
}

export enum SprayEventApplicationTypeDisplay {
    STANDARD = 'Standard',
    MULTIOPERATOR = 'Multi Operator',
    TWODIMENSION = '2D'
}

export enum SprayEventWeather {
    CALM = 'CALM',
    BREEZY = 'BREEZY',
    WINDY = 'WINDY',
    DRY = 'DRY',
    SHOWERY = 'SHOWERY',
    RAIN = 'RAIN'
}

export enum SprayEventDisposal {
    TARGET_CORP = 'TARGET_CORP',
    SHELTER_BELT = 'SHELTER_BELT',
    HEADLAND = 'HEADLAND',
    FALLOW_LAND = 'FALLOW_LAND',
    OTHER = 'OTHER'
}

export enum SprayEventOperatorsIndex {
    STANDARD = 0,
    TWODIMENSION = 0,
    OUTSIDE = 0,
    DOUBLE = 1
}

export interface SprayEventPostRequestPayload {
    entityId: SprayEventEntityId;
    startDate: SprayEventStartDate;
    endDate: SprayEventEndDate;
    sprayEntityIds: SprayEntityIds;
    sprayProducts: SprayProducts;
    sprayApplicationRate: SprayApplicationRate;
    sprayMethod: SprayMethod;
    sprayWaterSourceId: SprayWaterSourceId;
    justification: SprayEventJustification;
    technicalPersonName: SprayEventTechnicalPersonName;
    weather?: SprayEventWeather;
    isFinalSpray: SprayEventIsFinalSpray;
    isWeedSpray: SprayEventIsWeedSpray;
    applicationType: SprayEventApplicationType;
    sprayEventOperations: SprayEventOperations;
}

export interface SprayEventPatchRequestPayload
    extends SprayEventPostRequestPayload {
    sprayEventUuid: SprayEventUuid;
}

export interface SprayEventJob extends DocumentSet {
    assignedUsers: {
        email: string;
        id: SprayApplicatorId;
    }[];
    jobType: 'INSPECTION' | 'TIMESHEET';
    jobState: 'COMPLETE' | 'INCOMPLETE';
}

export interface SprayEvent {
    entityId: SprayEventEntityId;
    id: SprayEventId;
    schemaId: SprayEventSchemaId;
    startDate: SprayEventStartDate;
    jobs: SprayEventJob[];
    entityName: EntityName;
    entityPathNames: EntityPathNames;
    uuid: SprayEventUuid;
    sprayEvent: {
        applicationRate: SprayApplicationRate;
        eventId: SprayEventId;
        sprayMethod: SprayMethod;
        sprayWaterSourceId: SprayWaterSourceId;
        sprayProducts: SprayProduct[];
        sprayEntities: SprayEventEntities;
        justification: SprayEventJustification;
        technicalPersonName: SprayEventTechnicalPersonName;
        weather?: SprayEventWeather;
        isFinalSpray: SprayEventIsFinalSpray;
        isReadyToSubmit?: SprayEventIsReadyToSubmit;
        isWeedSpray: SprayEventIsWeedSpray;
        isSubmitted: SprayEventIsSubmitted;
        applicationType: SprayEventApplicationType;
        sprayEventOperations: SprayEventOperations;
    };
}

export type SprayEventsState = Record<SprayEventUuid, SprayEvent>;

export interface SprayEventsProps {
    sprayEventId: SprayEventId;
}

export interface DeleteSprayEventPayload {
    uuids: SprayEventUuid[];
}

export interface CompleteSprayEventsPayload {
    uuids: SprayEventUuid[];
    weatherUuids?: Record<SprayEventUuid, SprayEventWeather>;
    disposalUuids?: Record<SprayEventUuid, SprayEventDisposal>;
}

export interface UncompleteSprayEventPayload {
    uuid: SprayEventUuid;
}

export interface SuccededDeleteSprayEventPayload {
    uuids: SprayEventUuid[];
}

export interface RequestLoadSprayEventsAction {
    type: typeof SPRAY_EVENTS_LOAD_REQUESTED;
}

export interface SucceededLoadSprayEventsAction {
    type: typeof SPRAY_EVENTS_LOAD_SUCCEEDED;
    payload: AxiosResponse;
}

export interface FailedLoadTimesheetsAction {
    type: typeof SPRAY_EVENTS_LOAD_FAILED;
    payload: AxiosError;
    error: true;
}

export interface CanceledLoadSprayEventsAction {
    type: typeof SPRAY_EVENTS_LOAD_CANCELED;
}

export interface RequestCreateSprayEventAction {
    type: typeof SPRAY_EVENT_CREATE_REQUESTED;
    payload: SprayEventPostRequestPayload;
}

export interface RequestUpdateSprayEventAction {
    type: typeof SPRAY_EVENT_UPDATE_REQUESTED;
    payload: SprayEventPatchRequestPayload;
}

export interface SucceededUpdateSprayEventAction {
    type: typeof SPRAY_EVENT_UPDATE_SUCCEEDED;
    payload: AxiosResponse;
}

export interface FailedUpdateTimesheetAction {
    type: typeof SPRAY_EVENT_UPDATE_FAILED;
    payload: AxiosError;
    error: true;
}

export interface SucceededCreateSprayEventAction {
    type: typeof SPRAY_EVENT_CREATE_SUCCEEDED;
    payload: AxiosResponse;
}

export interface FailedCreateTimesheetAction {
    type: typeof SPRAY_EVENT_CREATE_FAILED;
    payload: AxiosError;
    error: true;
}

export interface SetSprayEventsAction {
    type: typeof SPRAY_EVENTS_SET;
    payload: SprayEventsState;
}

export interface DeleteSprayEventsAction {
    type: typeof SPRAY_EVENTS_DELETE;
    payload: DeleteSprayEventPayload;
}

export interface SetSprayEventAction {
    type: typeof SPRAY_EVENT_SET;
    payload: SprayEvent;
}

export interface RequestDeleteSprayEventAction {
    type: typeof SPRAY_EVENT_DELETE_REQUESTED;
    payload: DeleteSprayEventPayload;
}

export interface FailedDeleteSprayEventAction {
    type: typeof SPRAY_EVENT_DELETE_FAILED;
    payload: AxiosError;
    error: true;
}

export interface SucceededDeleteSprayEventAction {
    type: typeof SPRAY_EVENT_DELETE_SUCCEEDED;
    payload: SuccededDeleteSprayEventPayload;
}

export interface RequestCompleteSprayEventsAction {
    type: typeof SPRAY_EVENTS_COMPLETE_REQUESTED;
    payload: CompleteSprayEventsPayload;
}

export interface FailedCompleteSprayEventsAction {
    type: typeof SPRAY_EVENTS_COMPLETE_FAILED;
    payload: AxiosError;
    error: true;
}

export interface SucceededCompleteSprayEventsAction {
    type: typeof SPRAY_EVENTS_COMPLETE_SUCCEEDED;
}

export interface RequestUncompleteSprayEventAction {
    type: typeof SPRAY_EVENT_UNCOMPLETE_REQUESTED;
    payload: UncompleteSprayEventPayload;
}

export interface SucceededUncompleteSprayEventAction {
    type: typeof SPRAY_EVENT_UNCOMPLETE_SUCCEEDED;
    payload: AxiosResponse;
}

export interface FailedUncompleteSprayEventAction {
    type: typeof SPRAY_EVENT_UNCOMPLETE_FAILED;
    payload: AxiosError;
    error: true;
}

export type SprayEventsActions =
    | SetSprayEventsAction
    | DeleteSprayEventsAction
    | SetSprayEventAction
    | SucceededDeleteSprayEventAction;

export type SprayEventFilterType =
    | typeof DATE_FILTER_TYPE
    | typeof EQUIPMENT_FILTER_TYPE
    | typeof PRODUCT_FILTER_TYPE
    | typeof DATE_RANGE_FILTER_TYPE
    | typeof APPLICATION_RATE_FILTER_TYPE
    | typeof ENTITIES_FILTER_TYPE
    | typeof INNER_ENTITIES_FILTER_TYPE;

export interface SprayEventSearchFilters {
    sprayProductId?: number;
    sprayEquipmentSetId?: number;
    applicationRate?: number;
    startDate?: string;
    endDate?: string;
    sprayEntityId?: number;
    sprayInnerEntityId?: number;
}
